/**
 * The invitations waiting for the signed-in person's answer (ADR-0067, amended 2026-09-26).
 *
 * An invitation is accepted or declined by the person it names, never claimed silently. Two kinds
 * arrive here, from the session envelope:
 *
 * - **own** — a new person signed in with an invited email. It is the only access they have, and
 *   nothing else works until they accept it; declining closes it and signs them out.
 * - **offer** — somebody who already has an account signed in with an invited email. Accepting
 *   adds its areas to that account.
 *
 * The inviter's label and the areas are all it shows: no email, theirs or anyone's.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { InvitationView } from '../api/client.js';
import { answerInvitation } from '../api/people.js';
import { useSession } from '../auth/session.js';
import { listOf } from '../i18n/names.js';
import { RefusalAlert } from './refusal-alert.js';


export function InvitationsCard({ invitations }: { readonly invitations: readonly InvitationView[] }): React.JSX.Element | null {
  const { t, i18n } = useTranslation();
  const { refresh } = useSession();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  if (invitations.length === 0) return null;

  async function answer(invitationId: string, verb: 'accept' | 'decline'): Promise<void> {
    setBusy(invitationId);
    setError(null);
    try {
      await answerInvitation(invitationId, verb);
      await refresh();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="card" data-testid="invitations">
      <h2>{t('invitation.title')}</h2>
      {invitations.map((invitation) => (
        <div key={invitation.invitationId} className="row" data-testid={`invitation-${invitation.invitationId}`}>
          <p>
            {invitation.invitedBy
              ? t('invitation.from', { label: invitation.label ?? '', inviter: invitation.invitedBy })
              : t('invitation.named', { label: invitation.label ?? '' })}
          </p>
          <p>
            {t('invitation.areas', {
              areas: listOf(invitation.facets.map((facet) => t(`ops.facet.${facet}`)), i18n.language),
            })}
          </p>
          <p className="muted">{t(invitation.kind === 'own' ? 'invitation.ownHelp' : 'invitation.offerHelp')}</p>
          <div className="row">
            <button
              type="button"
              className="btn btn--primary"
              disabled={busy !== null}
              onClick={() => void answer(invitation.invitationId, 'accept')}
            >
              {t('invitation.accept')}
            </button>
            <button
              type="button"
              className="btn"
              disabled={busy !== null}
              onClick={() => void answer(invitation.invitationId, 'decline')}
            >
              {t('invitation.decline')}
            </button>
          </div>
        </div>
      ))}
      {error !== null && (
        <RefusalAlert
          className="notice notice--bad"
          error={error}
          options={{
            audience: 'ops',
            namespaces: ['invitation.issue', 'ops.people.issue'],
            label: (path) => (path === 'invitationId' ? t('invitation.label') : path),
          }}
        />
      )}
    </section>
  );
}
